import React, { useEffect, useState } from "react";
import axios from "../../../axios";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Spinner,
  useToast,
} from "@chakra-ui/react";
import WinnerCard from "./Winners";

const getPlayerUserId = (player) =>
  player?.userId?._id ||
  player?.userId ||
  player?.user?._id ||
  player?.user ||
  player?._id;

const getIdText = (value) => {
  if (!value) return "";
  if (typeof value === "object") return String(value._id || value.id || "");
  return String(value);
};

const pickFirst = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "");

const extractAnswerRows = (entry) => {
  if (!entry) return [];
  if (Array.isArray(entry)) return entry;

  const rows =
    entry.answerKey ||
    entry.answers ||
    entry.submittedAnswers ||
    entry.questionAnswers ||
    entry.questions ||
    entry.responses;

  if (Array.isArray(rows)) return rows;
  if (entry.question || entry.questionText || entry.selectedAnswer) return [entry];

  return [];
};

const matchesPlayer = (entry, player) => {
  const playerId = getIdText(getPlayerUserId(player));
  const entryIds = [
    entry?.userId,
    entry?.user?._id,
    entry?.user,
    entry?.playerId,
    entry?.participantId,
    entry?.userContest?.user,
  ].map(getIdText);

  return (
    (playerId && entryIds.includes(playerId)) ||
    (player?.mobile &&
      String(entry?.mobile || entry?.user?.mobile || "") === String(player.mobile)) ||
    (player?.name && String(entry?.name || entry?.user?.name || "") === String(player.name))
  );
};

const getRowsForPlayer = (payload, player) => {
  const root = payload?.result || payload?.data || payload;
  const playerId = getIdText(getPlayerUserId(player));

  if (!root) return [];

  if (!Array.isArray(root) && typeof root === "object") {
    if (root[playerId]) return extractAnswerRows(root[playerId]);

    const rootRows = extractAnswerRows(root);
    if (rootRows.length) {
      const matchedRows = rootRows.filter((row) => matchesPlayer(row, player));
      return matchedRows.length ? matchedRows : rootRows;
    }

    const nestedList = root.users || root.players || root.participants || root.leaderboard;
    if (Array.isArray(nestedList)) {
      const matchedEntry = nestedList.find((entry) => matchesPlayer(entry, player));
      return extractAnswerRows(matchedEntry);
    }

    return [];
  }

  if (Array.isArray(root)) {
    const matchedEntries = root.filter((entry) => matchesPlayer(entry, player));
    if (matchedEntries.length) {
      return matchedEntries.flatMap(extractAnswerRows);
    }

    return root.flatMap(extractAnswerRows);
  }

  return [];
};

const getQuestionText = (row) =>
  pickFirst(
    row?.question,
    row?.questionText,
    row?.questionTitle,
    row?.questionId?.question,
    row?.contestQuestion?.question,
  ) || "Question not available";

const getSelectedAnswer = (row) =>
  pickFirst(row?.selectedAnswer, row?.userAnswer, row?.answerGiven, row?.submittedAnswer, row?.answer);

const getCorrectAnswer = (row) =>
  pickFirst(
    row?.correctAnswer,
    row?.rightAnswer,
    row?.questionId?.answer,
    row?.contestQuestion?.answer,
    row?.answer,
  );

const getOptions = (row) => {
  const options = pickFirst(
    row?.options,
    row?.optionsHin,
    row?.questionId?.options,
    row?.questionId?.optionsHin,
    row?.contestQuestion?.options,
    row?.contestQuestion?.optionsHin,
  );

  if (Array.isArray(options)) return options;
  if (typeof options === "string") {
    return options
      .split(",")
      .map((option) => option.trim())
      .filter(Boolean);
  }

  return [];
};

const formatContestDate = (dateValue) => {
  if (!dateValue) return "-";

  return new Date(dateValue).toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const getSafePdfText = (value) => {
  if (value === undefined || value === null || value === "") return "-";
  return String(value);
};

const getPdfFileName = (contestName) => {
  const safeName = String(contestName || "contest-result")
    .trim()
    .replace(/[^a-z0-9]+/gi, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase();

  return `${safeName || "contest-result"}-result.pdf`;
};

const ViewContestResult = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();

  const [contest, setContest] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [totalParticipants, setTotalParticipants] = useState(0);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [answerRows, setAnswerRows] = useState([]);
  const [questionCount, setQuestionCount] = useState(0);
  const [isAnswerKeyLoading, setIsAnswerKeyLoading] = useState(false);
  const [isAnswerKeyOpen, setIsAnswerKeyOpen] = useState(false);

  const backToContestList = () => {
    const returnPage = Number(location.state?.returnToContestPage);
    const pageQuery =
      Number.isInteger(returnPage) && returnPage > 1 ? `?page=${returnPage}` : "";

    navigate(`/dash/view-contest${pageQuery}`);
  };

  const handleCloseAnswerKey = () => {
    setIsAnswerKeyOpen(false);
    setSelectedPlayer(null);
    setAnswerRows([]);
  };

  useEffect(() => {
    axios
      .get(`/contest-result-summary-for-admin?contestId=${id}`)
      .then((res) => {
        if (res.data.success) {

          const data = res.data.result;
          

          setContest(data.contest);
          setLeaderboard(data.users || []);
          setTotalParticipants(data.totalParticipants || 0);

        }
      })
      .catch((err) => {
        console.log(err);

        toast({
          title: "Failed to fetch contest result",
          status: "error",
          duration: 4000,
          isClosable: true,
          position: "top",
        });
      });
  }, [id, toast]);

  useEffect(() => {
    if (!id) return;

    axios
      .get(`getContestQuestionsByContestId/${id}`)
      .then((res) => {
        const result = res.data?.result;
        const questions =
          Array.isArray(result)
            ? result
            : Array.isArray(result?.questions)
            ? result.questions
            : [];

        setQuestionCount(questions.length);
      })
      .catch(() => {
        setQuestionCount(0);
      });
  }, [id]);

const topThree = [...leaderboard]
  .sort((a, b) => a.rank - b.rank)
  .slice(0, 3);

const winner1 = topThree[0];
const winner2 = topThree[1];
const winner3 = topThree[2];
const selectedPlayerId = getPlayerUserId(selectedPlayer);

const isSelectedPlayer = (player) =>
  selectedPlayerId && getPlayerUserId(player) === selectedPlayerId;

const handleShowAnswerKey = async (player) => {
  console.log("answer keysss")
  try {
    setSelectedPlayer(player);
    setAnswerRows([]);
    setIsAnswerKeyOpen(true);
    setIsAnswerKeyLoading(true);

    const res = await axios.get(`contest/${id}/answer-key/${player.userId}`);

    const rows =
      res.data?.result?.questions ||
      res.data?.result ||
      [];

    const parsedRows = Array.isArray(rows) ? rows : extractAnswerRows(rows);
    setAnswerRows(parsedRows);
  } catch (err) {
    toast({
      title: err?.response?.data?.message || "Failed to fetch answer key",
      status: "error",
      duration: 4000,
      isClosable: true,
      position: "top",
    });
  } finally {
    setIsAnswerKeyLoading(false);
  }
};

const handleDownloadPdf = async () => {
  const { default: jsPDF } = await import("jspdf");

  const doc = new jsPDF({
    orientation: "landscape",
    unit: "pt",
    format: "a4",
  });

  const margin = 36;
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const tableWidth = pageWidth - margin * 2;
  const rowHeight = 24;
  const headerHeight = 24;
  const columns = [
    { label: "Rank", key: "rank", width: 42 },
    { label: "Name", key: "name", width: 128 },
    { label: "Mobile", key: "mobile", width: 90 },
    { label: "Right", key: "rightAnswers", width: 52 },
    { label: "Wrong", key: "wrongAnswers", width: 56 },
    { label: "Attempt", key: "attempt", width: 62 },
    { label: "Accuracy", key: "accuracy", width: 70 },
    { label: "Score", key: "score", width: 56 },
  ];

  const totalColumnWidth = columns.reduce((sum, column) => sum + column.width, 0);
  const scale = tableWidth / totalColumnWidth;
  const scaledColumns = columns.map((column) => ({
    ...column,
    width: column.width * scale,
  }));

  const drawHeader = () => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.text("Contest Result", margin, 42);

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(`Contest Name: ${getSafePdfText(contest?.contestName)}`, margin, 66);
    doc.text(`Date: ${formatContestDate(contest?.contestDate)}`, margin, 82);
    doc.text(`Prize: ${getSafePdfText(contest?.prizePoolCoin || 0)}`, margin, 98);
    doc.text(`Total Participants: ${getSafePdfText(totalParticipants)}`, margin, 114);
  };

  const drawTableHeader = (startY) => {
    let x = margin;

    doc.setFillColor(77, 44, 94);
    doc.rect(margin, startY, tableWidth, headerHeight, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);

    scaledColumns.forEach((column) => {
      doc.text(column.label, x + 4, startY + 15);
      x += column.width;
    });

    doc.setTextColor(0, 0, 0);
  };

  const drawRow = (player, rowIndex, startY) => {
    let x = margin;

    if (rowIndex % 2 === 0) {
      doc.setFillColor(248, 248, 248);
      doc.rect(margin, startY, tableWidth, rowHeight, "F");
    }

    doc.setDrawColor(220, 220, 220);
    doc.rect(margin, startY, tableWidth, rowHeight);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);

    const values = {
      rank: getSafePdfText(player.rank),
      name: getSafePdfText(player.name || "User Deleted"),
      mobile: getSafePdfText(player.mobile),
      rightAnswers: getSafePdfText(player.rightAnswers),
      wrongAnswers: getSafePdfText(player.wrongAnswers),
      attempt: getSafePdfText(
        Number(player.rightAnswers || 0) + Number(player.wrongAnswers || 0),
      ),
      accuracy: `${getSafePdfText(player.accuracy)}%`,
      score: getSafePdfText(player.score),
    };

    scaledColumns.forEach((column) => {
      const text = doc.splitTextToSize(values[column.key], column.width - 8)[0] || "-";
      doc.text(text, x + 4, startY + 15);
      x += column.width;
    });
  };

  drawHeader();

  let y = 138;
  drawTableHeader(y);
  y += headerHeight;

  leaderboard.forEach((player, index) => {
    if (y + rowHeight > pageHeight - margin) {
      doc.addPage();
      y = margin;
      drawTableHeader(y);
      y += headerHeight;
    }

    drawRow(player, index, y);
    y += rowHeight;
  });

  doc.save(getPdfFileName(contest?.contestName));
};

const renderAnswerKeyDetails = () => {
  if (!isAnswerKeyOpen || !selectedPlayer) return null;

  return (
    <div className="mt-4 bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Answer Key</h3>
          <p className="text-sm text-gray-500">
            {selectedPlayer?.name || "User"} - {selectedPlayer?.mobile || "No mobile"}
          </p>
        </div>
        <Button colorScheme="gray" size="sm" onClick={handleCloseAnswerKey}>
          Close
        </Button>
      </div>

      {isAnswerKeyLoading ? (
        <div className="flex items-center justify-center py-10">
          <Spinner />
        </div>
      ) : answerRows.length ? (
        <div className="space-y-4">
          {answerRows.map((row, index) => {
            const selectedAnswer = getSelectedAnswer(row);
            const correctAnswer = getCorrectAnswer(row);
            const options = getOptions(row);
            const isCorrect =
              selectedAnswer?.toString().trim().toLowerCase() ===
              correctAnswer?.toString().trim().toLowerCase();

            return (
              <div
                key={row?._id || index}
                className="rounded-lg border border-gray-200 bg-gray-50 p-4 shadow-sm"
              >
                <p className="text-sm font-semibold text-gray-500">Question {index + 1}</p>
                <p className="mt-1 font-bold text-gray-900">{getQuestionText(row)}</p>

                {options.length ? (
                  <div className="mt-3 grid gap-2 text-sm">
                    {options.map((option, optionIndex) => {
                      const optionText = option?.toString?.() || "";
                      const matchesCorrect =
                        optionText.trim().toLowerCase() ===
                        correctAnswer?.toString().trim().toLowerCase();
                      const matchesSelected =
                        optionText.trim().toLowerCase() ===
                        selectedAnswer?.toString().trim().toLowerCase();

                      return (
                        <div
                          key={`${row?._id || index}-${optionIndex}`}
                          className={`rounded-md border px-3 py-2 ${
                            matchesCorrect
                              ? "border-green-500 bg-green-50 text-green-800"
                              : matchesSelected
                              ? "border-blue-500 bg-blue-50 text-blue-800"
                              : "border-gray-200 bg-white text-gray-700"
                          }`}
                        >
                          <span className="font-semibold mr-2">
                            {String.fromCharCode(65 + optionIndex)}.
                          </span>
                          {optionText}
                          {matchesCorrect ? " (Correct)" : ""}
                          {matchesSelected && !matchesCorrect ? " (User Answer)" : ""}
                        </div>
                      );
                    })}
                  </div>
                ) : null}

                <div className="mt-3 grid gap-2 text-sm">
                  <div
                    className={`rounded-md border px-3 py-2 ${
                      isCorrect
                        ? "border-green-500 bg-green-50 text-green-800"
                        : "border-red-500 bg-red-50 text-red-800"
                    }`}
                  >
                    User Answer: {selectedAnswer || "Not answered"}
                  </div>
                  <div className="rounded-md border border-green-500 bg-green-50 px-3 py-2 text-green-800">
                    Correct Answer: {correctAnswer || "Not available"}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="rounded-lg border border-dashed border-gray-300 p-8 text-center text-gray-500">
          Answer key not found for this user.
        </div>
      )}
    </div>
  );
};

  if (!contest) {
    return (
      <p className="text-center mt-10">
        Loading...
      </p>
    );
  }


  return (
    <div className="m-6 py-8 bg-gray-50 rounded-lg shadow-md">
      <div className="mb-4 px-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <Button colorScheme="purple" variant="outline" onClick={backToContestList}>
            Back to Contests
          </Button>
          <Button
            colorScheme="purple"
            onClick={handleDownloadPdf}
            isDisabled={!leaderboard.length}
          >
            PDF Download
          </Button>
        </div>
      </div>


      {/* Contest Details */}

      <div className="grid grid-cols-2 gap-4 mb-6 bg-white p-5 rounded-lg">


        <div>
          <p className="text-gray-500 text-sm">
            Contest Title
          </p>

          <p className="font-semibold">
            {contest.contestName}
          </p>
        </div>



        <div>
          <p className="text-gray-500 text-sm">
            Contest Date : {formatContestDate(contest.contestDate)}
          </p>

          <p className="font-semibold">
            {contest.createdOn
              ? new Date(contest.createdOn).toLocaleString()
              : "-"}
          </p>
        </div>



        <div>
          <p className="text-gray-500 text-sm">
            Prize Pool
          </p>

          <p className="font-semibold">
            {contest.prizePoolCoin || 0}
          </p>
        </div>



        <div>
          <p className="text-gray-500 text-sm">
            Total Participants
          </p>

          <p className="font-semibold">
            {totalParticipants}
          </p>
        </div>

      </div>



      {/* Leaderboard */}


     {/* Leaderboard */}

<div className="bg-white shadow rounded-lg p-6">

  <div className="bg-gray-100 rounded-xl p-8 mb-8">
  <h2 className="text-center text-2xl font-bold mb-8">
    Contest Leaderboard
  </h2>

  <div className="flex justify-center items-end gap-6">

    {/* Rank 2 */}
    <WinnerCard
      winner={winner2}
      rank={2}
    />

    {/* Rank 1 */}
    <WinnerCard
      winner={winner1}
      rank={1}
      isFirst
    />

    {/* Rank 3 */}
    <WinnerCard
      winner={winner3}
      rank={3}
    />

  </div>
</div>
  {/* Desktop Table */}

  <div className="hidden md:block overflow-x-auto">

    <table className="min-w-full border">

      <thead className="bg-gray-100">
        <tr>
          <th className="border px-4 py-2">Rank</th>
          <th className="border px-4 py-2">Name</th>
          <th className="border px-4 py-2">Mobile</th>
          <th className="border px-4 py-2">Score</th>
          <th className="border px-4 py-2">Accuracy</th>
          <th className="border px-4 py-2">Right</th>
          <th className="border px-4 py-2">Wrong</th>
          <th className="border px-4 py-2">Time</th>
          <th className="border px-4 py-2">Coins</th>
          <th className="border px-4 py-2">Answer Key</th>
        </tr>
      </thead>


      <tbody>

        {leaderboard.map((player,index)=>(
          <React.Fragment key={index}>
            <tr>
              <td className="border px-4 py-2 text-center">
                {player.rank}
              </td>

              <td className="border px-4 py-2 flex items-center gap-2">

              {
                player.avatar &&
                player.avatar !== "NA" &&
                player.avatar.startsWith("http") ?

                <img
                  src={player.avatar}
                  className="w-8 h-8 rounded-full object-cover"
                />

                :

                <div className="w-8 h-8 rounded-full bg-purple text-white flex items-center justify-center">
                  {player.name?.charAt(0)}
                </div>
              }


              {getPlayerUserId(player) ? (
                <Link
                  to={`/dash/view-user/${getPlayerUserId(player)}`}
                  className="font-semibold hover:underline"
                >
                  {player.name || "User Deleted"}
                </Link>
              ) : (
                player.name || "User Deleted"
              )}

            </td>


            <td className="border px-4 py-2 text-center">
              {player.mobile}
            </td>


            <td className="border px-4 py-2">
              {player.score}
            </td>


            <td className="border px-4 py-2">
              {player.accuracy}%
            </td>


            <td className="border px-4 py-2">
              {player.rightAnswers}
            </td>


            <td className="border px-4 py-2">
              {player.wrongAnswers}
            </td>


            <td className="border px-4 py-2">
              {player.timeTaken}s
            </td>


            <td className="border px-4 py-2">
              🪙 {player.coinsEarned}
            </td>

            <td className="border px-4 py-2 text-center">
              <Button
                size="sm"
                colorScheme="green"
                onClick={() => handleShowAnswerKey(player)}
              >
                View Answer Key
              </Button>
            </td>

          </tr>
          {isSelectedPlayer(player) && (
            <tr>
              <td colSpan={10} className="bg-gray-50 p-0">
                {renderAnswerKeyDetails()}
              </td>
            </tr>
          )}
          </React.Fragment>
        ))}
      </tbody>
    </table>

  </div>


  {/* Mobile Card View */}

  <div className="md:hidden space-y-4">


    {leaderboard.map((player,index)=>(


      <div
        key={index}
        className="border rounded-xl p-4 shadow-sm bg-gray-50"
      >


        {/* Top */}

        <div className="flex justify-between items-center mb-3">


          <div className="flex items-center gap-3">


            {
              player.avatar &&
              player.avatar !== "NA" &&
              player.avatar.startsWith("http") ?

              <img
                src={player.avatar}
                alt={player.name}
                className="w-12 h-12 rounded-full object-cover"
              />

              :

              <div className="w-12 h-12 rounded-full bg-purple text-white flex items-center justify-center text-lg font-bold">
                {player.name?.charAt(0)?.toUpperCase()}
              </div>

            }


            <div>

              {getPlayerUserId(player) ? (
                <Link
                  to={`/dash/view-user/${getPlayerUserId(player)}`}
                  className="block font-semibold text-gray-800 hover:underline"
                >
                  {player.name || "User Deleted"}
                </Link>
              ) : (
                <p className="font-semibold text-gray-800">
                  {player.name || "User Deleted"}
                </p>
              )}

              <p className="text-sm text-gray-500">
                Rank #{player.rank}
              </p>
              <p className="text-sm text-gray-500">
                {player.rank}
              </p>

            </div>


          </div>
          <div className="text-right">
<p className="font-bold text-purple">
              {player.mobile}
            </p>
          </div>


          <div className="text-right">

            <p className="font-bold text-purple">
              {player.score}
            </p>

            <p className="text-xs text-gray-500">
              Score
            </p>

          </div>


        </div>





        {/* Details */}

        <div className="grid grid-cols-2 gap-3 text-sm">


          <div className="bg-white p-2 rounded">
            <p className="text-gray-500">
              Accuracy
            </p>
            <b>
              {player.accuracy}%
            </b>
          </div>



          <div className="bg-white p-2 rounded">
            <p className="text-gray-500">
              Coins
            </p>
            <b>
              🪙 {player.coinsEarned}
            </b>
          </div>



          <div className="bg-white p-2 rounded">
            <p className="text-gray-500">
              Correct
            </p>
            <b>
              {player.rightAnswers}
            </b>
          </div>



          <div className="bg-white p-2 rounded">
            <p className="text-gray-500">
              Wrong
            </p>
            <b>
              {player.wrongAnswers}
            </b>
          </div>



          <div className="bg-white p-2 rounded">
            <p className="text-gray-500">
              Total Questions
            </p>
            <b>
              {questionCount}
            </b>
          </div>

          <div className="bg-white p-2 rounded col-span-1">
            <p className="text-gray-500">
              Time Taken
            </p>
            <b>
              {player.timeTaken}s
            </b>
          </div>

          <div className="col-span-2 flex justify-end">
            <Button
              size="sm"
              colorScheme="green"
              onClick={() => handleShowAnswerKey(player)}
            >
              View Answer Key
            </Button>
          </div>

        </div>

        {isSelectedPlayer(player) && renderAnswerKeyDetails()}

      </div>


    ))}


  </div>


</div>




    </div>
  );
};

export default ViewContestResult;
